import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, XCircle, Trash2, Inbox, ExternalLink, Mail, Loader2, MapPin, Calendar, Euro } from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "⏳ En attente", color: "bg-amber-50 text-amber-700" },
  approved: { label: "✅ Approuvée", color: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "❌ Rejetée", color: "bg-rose-50 text-rose-700" },
};

export default function AdminEventSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = filter === "all" ? "?status=all" : `?status=${filter}`;
    api.get(`/admin/event-submissions${q}`)
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (s) => {
    if (!window.confirm(`Approuver "${s.title}" ?\n\nElle sera créée en brouillon dans /admin/events. Vous pourrez la finaliser et la publier ensuite.`)) return;
    try { await api.post(`/admin/event-submissions/${s.id}/approve`); toast.success("Événement approuvé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const reject = async (s) => {
    const reason = window.prompt("Motif du rejet (sera envoyé par email à l'organisateur) :", "");
    if (reason === null) return;
    try { await api.post(`/admin/event-submissions/${s.id}/reject`, { reason }); toast.success("Soumission rejetée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer définitivement "${s.title}" ?`)) return;
    try { await api.delete(`/admin/event-submissions/${s.id}`); toast.success("Supprimée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><Inbox className="mr-2 inline h-7 w-7 text-wine-500" />Soumissions d&apos;événements</h1>
          <p className="mt-1 text-neutral-600">Modérez les événements soumis par les membres (gratuits) et le public (payants via package).</p>
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-neutral-200" role="tablist">
        {[
          { id: "pending", label: "En attente" },
          { id: "approved", label: "Approuvées" },
          { id: "rejected", label: "Rejetées" },
          { id: "all", label: "Toutes" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} className={`relative px-4 py-2.5 text-sm font-semibold ${filter === f.id ? "text-wine-700" : "text-neutral-500 hover:text-navy"}`} data-testid={`tab-${f.id}`}>
            {f.label}
            {filter === f.id && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-wine-500" />}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Événement</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Organisateur</th>
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500"><Loader2 className="inline h-5 w-5 animate-spin" /></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">Aucune soumission</td></tr>}
            {!loading && items.map((s) => {
              const st = STATUS_LABELS[s.status] || { label: s.status, color: "bg-neutral-100" };
              return (
                <tr key={s.id} className="border-t border-neutral-100" data-testid={`event-sub-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{s.title}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-neutral-500">
                      <MapPin className="h-3 w-3" /> {s.location_city}, {s.location_country} {s.online && <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold text-blue-700">EN LIGNE</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-1 text-neutral-700"><Calendar className="h-3 w-3" /> {s.start_date}</div>
                    {s.end_date && s.end_date !== s.start_date && <div className="text-neutral-400">→ {s.end_date}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-semibold">{s.organizer_name}</div>
                    <div className="text-neutral-500">{s.contact_email}</div>
                    {s.organizer_company && <div className="text-neutral-400 italic">{s.organizer_company}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {s.source === "member" && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">🟢 Membre (gratuit)</span>}
                    {s.source === "paid_public" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                        <Euro className="h-2.5 w-2.5" /> {s.package_title} — {(s.package_price_eur / 100).toFixed(0)}€
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.color}`}>{st.label}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setDetail(s)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Voir détails" data-testid={`view-${s.id}`}>👁️</button>
                      {s.status === "pending" && (
                        <>
                          <button onClick={() => approve(s)} className="grid h-7 w-7 place-items-center rounded-full text-emerald-600 hover:bg-emerald-50" title="Approuver" data-testid={`approve-${s.id}`}><CheckCircle2 className="h-3.5 w-3.5" /></button>
                          <button onClick={() => reject(s)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Rejeter" data-testid={`reject-${s.id}`}><XCircle className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                      {s.registration_url && <a href={s.registration_url} target="_blank" rel="noreferrer" className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Lien inscription"><ExternalLink className="h-3.5 w-3.5" /></a>}
                      <a href={`mailto:${s.contact_email}`} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Email"><Mail className="h-3.5 w-3.5" /></a>
                      <button onClick={() => remove(s)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-400 hover:bg-rose-50 hover:text-rose-600" title="Supprimer"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="event-sub-detail">
            <div className="flex items-start justify-between border-b border-neutral-100 pb-3">
              <div>
                <h3 className="font-serif text-2xl text-navy">{detail.title}</h3>
                <p className="mt-1 text-sm text-neutral-500">{detail.location_city}, {detail.location_country} {detail.online && "• En ligne"}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-neutral-400 hover:text-neutral-700">✕</button>
            </div>
            {detail.image_url && <img src={detail.image_url} alt={detail.title} className="mt-4 max-h-60 w-full rounded-xl object-cover" />}
            <p className="mt-4 whitespace-pre-line text-sm text-neutral-700">{detail.description}</p>
            <div className="mt-4 grid gap-2 text-xs">
              <div><strong>📅</strong> {detail.start_date} {detail.end_date && detail.end_date !== detail.start_date && `→ ${detail.end_date}`}</div>
              {detail.venue && <div><strong>📍</strong> {detail.venue}</div>}
              {detail.registration_url && <div><strong>🎟️</strong> <a href={detail.registration_url} target="_blank" rel="noreferrer" className="text-wine-700 hover:underline break-all">{detail.registration_url}</a></div>}
              <div><strong>👤</strong> {detail.organizer_name}{detail.organizer_company && ` — ${detail.organizer_company}`}</div>
              <div><strong>📧</strong> {detail.contact_email}</div>
              {detail.contact_phone && <div><strong>📞</strong> {detail.contact_phone}</div>}
              <div><strong>🏷️</strong> {detail.category}</div>
              <div><strong>💸</strong> {detail.source === "paid_public" ? `Package payé : ${detail.package_title} (${(detail.package_price_eur / 100).toFixed(0)}€)` : "Gratuit (membre)"}</div>
              {detail.reject_reason && <div className="rounded-xl bg-rose-50 p-3 text-rose-800"><strong>Motif de rejet :</strong> {detail.reject_reason}</div>}
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
